/**
 * Background design library expansion (2026-11-04).
 *
 * New pickable check backgrounds — geometric PATTERNS and paper
 * TEXTURES — drawn procedurally on the render canvas by the same
 * offscreen pass that draws every existing texture in CheckBuilder,
 * so they appear identically on screen and in canvas exports. No
 * external image assets, no security-engine involvement: these are
 * decorative paper designs that live UNDER the check furniture and
 * are clipped at textureBottom, exactly like the imported-pattern
 * layer — they never enter the MICR clear band.
 *
 * Colours come from the active design's colourway (primary /
 * secondary / accent), so every pattern combines with every bank
 * template and premium design instead of carrying a fixed palette.
 * Scatter designs use a seeded RNG (same sin-hash idiom as the
 * linen fibers) so a design renders the same on every pass —
 * screen, re-render and export never reshuffle.
 */

export interface BackgroundDesignEntry {
    id: string;
    name: string;
    icon: string;
    kind: 'pattern' | 'texture';
}

export const NEW_BACKGROUND_DESIGNS: BackgroundDesignEntry[] = [
    // ---- Patterns -------------------------------------------------
    { id: 'lattice', name: 'Geometric Lattice', icon: '🔷', kind: 'pattern' },
    { id: 'wave_ribbon', name: 'Ribbon Waves', icon: '🎗️', kind: 'pattern' },
    { id: 'harlequin', name: 'Harlequin Diamonds', icon: '◆', kind: 'pattern' },
    { id: 'pinstripe', name: 'Banker Pinstripe', icon: '📏', kind: 'pattern' },
    { id: 'dot_grid', name: 'Halftone Dot Grid', icon: '⠿', kind: 'pattern' },
    { id: 'contour', name: 'Contour Lines', icon: '🗺️', kind: 'pattern' },
    { id: 'starburst', name: 'Starburst Rays', icon: '☀️', kind: 'pattern' },
    { id: 'basket_weave', name: 'Basket Weave', icon: '🧺', kind: 'pattern' },
    { id: 'chevron', name: 'Chevron Bands', icon: '⌄', kind: 'pattern' },
    { id: 'fan_scallop', name: 'Scallop Fans', icon: '🐚', kind: 'pattern' },
    // ---- Textures -------------------------------------------------
    { id: 'marble_vein', name: 'Veined Marble', icon: '🏛️', kind: 'texture' },
    { id: 'granite_speckle', name: 'Granite Speckle', icon: '🪨', kind: 'texture' },
    { id: 'herringbone', name: 'Herringbone Weave', icon: '🐟', kind: 'texture' },
    { id: 'satin_sheen', name: 'Satin Sheen', icon: '✨', kind: 'texture' },
    // ANB Bank specimen face (fidelity rebuild, 2026-11-04): a full
    // scenic — pale cloud sky over a golden wheat field. Fixed
    // artwork, not a colourway pattern (see drawScenicField).
    { id: 'scenic_field', name: 'Scenic Sky & Field', icon: '🌾', kind: 'texture' }
];

/** Entries in the shape CheckBuilder's TEXTURES picker consumes. */
export const NEW_TEXTURE_ENTRIES = NEW_BACKGROUND_DESIGNS.map(d => ({ id: d.id, name: d.name, icon: d.icon }));

const NEW_IDS = new Set(NEW_BACKGROUND_DESIGNS.map(d => d.id));

export function isNewBackgroundDesign(id: string | null | undefined): boolean {
    return !!id && NEW_IDS.has(id);
}

export interface BackgroundGeom {
    width: number;
    /** Full canvas height (page decoration reference). */
    height: number;
    /** Patterns stop here — the MICR clear band starts at this y. */
    textureBottom: number;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
}

/** Deterministic scatter source (sin-hash, same idiom as linen). */
const seeded = (seed: number) => (n: number) => {
    const x = Math.sin(n * 9301 + seed) * 10000;
    return x - Math.floor(x);
};

type Ctx = CanvasRenderingContext2D;

const drawLattice = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    const step = 16;
    ctx.lineWidth = 0.6;
    // Rising diagonals (primary)
    ctx.strokeStyle = `${primaryColor}2e`;
    for (let i = -B; i < W + B; i += step) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + B, B); ctx.stroke();
    }
    // Falling diagonals (secondary, lighter)
    ctx.strokeStyle = `${secondaryColor}22`;
    for (let i = 0; i < W + B; i += step) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i - B, B); ctx.stroke();
    }
    // Nodes where the lattice crosses, every other intersection
    ctx.fillStyle = `${primaryColor}30`;
    for (let x = 0; x <= W; x += step * 2) {
        for (let y = 0; y <= B; y += step) {
            ctx.beginPath(); ctx.arc(x + ((y / step) % 2) * step, y, 1.1, 0, Math.PI * 2); ctx.fill();
        }
    }
};

const drawWaveRibbon = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Horizontal ribbon pairs: two phase-shifted sine strands per
    // band, bands on an even pitch — a woven ribbon field, distinct
    // from the guilloche rosettes of guilloche_bg.
    const pitch = 22;
    ctx.lineWidth = 0.8;
    for (let band = 0, row = 0; band < B + pitch; band += pitch, row++) {
        const amp = 5 + (row % 3);
        ctx.strokeStyle = row % 2 === 0 ? `${primaryColor}30` : `${secondaryColor}26`;
        ctx.beginPath();
        for (let x = 0; x <= W; x += 6) {
            const y = band + Math.sin(x * 0.021 + row) * amp;
            if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.strokeStyle = row % 2 === 0 ? `${secondaryColor}20` : `${primaryColor}1e`;
        ctx.beginPath();
        for (let x = 0; x <= W; x += 6) {
            const y = band + 6 + Math.sin(x * 0.021 + row + Math.PI) * amp;
            if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
    }
};

const drawHarlequin = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Classic harlequin: tall diamonds alternating the two colourway
    // tones at paper-friendly alpha, every third column left bare.
    const dw = 30, dh = 19;
    for (let row = 0, cy = 0; cy < B + dh; row++, cy += dh) {
        for (let col = 0, cx = (row % 2) * (dw / 2); cx < W + dw; col++, cx += dw) {
            const tone = (col + row) % 3;
            if (tone === 2) continue;
            ctx.fillStyle = tone === 0 ? `${primaryColor}17` : `${secondaryColor}15`;
            ctx.beginPath();
            ctx.moveTo(cx, cy - dh / 2);
            ctx.lineTo(cx + dw / 2, cy);
            ctx.lineTo(cx, cy + dh / 2);
            ctx.lineTo(cx - dw / 2, cy);
            ctx.closePath();
            ctx.fill();
        }
    }
    ctx.strokeStyle = `${primaryColor}14`;
    ctx.lineWidth = 0.4;
    for (let cy = 0; cy < B + dh; cy += dh) {
        ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(W, cy); ctx.stroke();
    }
};

const drawPinstripe = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor, accentColor } = g;
    // Banker pinstripe: paired hairlines on a steady pitch, every
    // fifth pair picked out in the accent tone.
    const pitch = 15;
    ctx.lineWidth = 0.7;
    for (let x = 4, i = 0; x < W; x += pitch, i++) {
        const isAccent = i % 5 === 4;
        ctx.strokeStyle = isAccent ? `${accentColor}2c` : `${primaryColor}28`;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, B); ctx.stroke();
        ctx.strokeStyle = isAccent ? `${accentColor}1c` : `${secondaryColor}1a`;
        ctx.beginPath(); ctx.moveTo(x + 2.5, 0); ctx.lineTo(x + 2.5, B); ctx.stroke();
    }
};

const drawDotGrid = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Halftone dot grid: staggered rows, dot radius breathing with a
    // diagonal sine field so the sheet reads as a printed screen.
    const step = 11;
    for (let row = 0, y = 6; y < B; row++, y += step) {
        const off = (row % 2) * (step / 2);
        for (let x = 6 + off; x < W; x += step) {
            const r = 0.7 + 1.5 * (0.5 + 0.5 * Math.sin((x + y) * 0.018));
            ctx.fillStyle = (row + Math.round(x / step)) % 4 === 0 ? `${secondaryColor}30` : `${primaryColor}2c`;
            ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        }
    }
};

const drawContour = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Topographic contours: wobbly concentric rings around three
    // seeded centres, alternating the colourway tones per ring.
    const rnd = seeded(77);
    const centres = [
        { x: W * (0.18 + rnd(1) * 0.1), y: B * 0.36 },
        { x: W * (0.55 + rnd(2) * 0.1), y: B * 0.62 },
        { x: W * (0.84 + rnd(3) * 0.08), y: B * 0.30 }
    ];
    ctx.lineWidth = 0.55;
    centres.forEach((c, ci) => {
        for (let ring = 1; ring <= 9; ring++) {
            const base = ring * 13;
            ctx.strokeStyle = ring % 2 === 0 ? `${primaryColor}26` : `${secondaryColor}20`;
            ctx.beginPath();
            for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.09) {
                const wob = Math.sin(a * 3 + ci * 2 + ring) * 3.2 + Math.sin(a * 5 + ring * 1.7) * 1.6;
                const r = base + wob;
                const x = c.x + r * Math.cos(a) * 1.35;
                const y = c.y + r * Math.sin(a);
                if (a === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
    });
};

const drawStarburst = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor, accentColor } = g;
    // Fine rays from a medallion point above the top-centre of the
    // face — the classic safety-paper sunburst.
    const cx = W / 2, cy = -30;
    ctx.lineWidth = 0.5;
    for (let deg = 18; deg <= 162; deg += 1.6) {
        const a = (deg * Math.PI) / 180;
        const x2 = cx + Math.cos(a) * (W * 0.85);
        const y2 = cy + Math.sin(a) * (W * 0.85);
        ctx.strokeStyle = Math.round(deg) % 8 === 0 ? `${accentColor}1e` : `${primaryColor}17`;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(x2, y2); ctx.stroke();
    }
    ctx.strokeStyle = `${secondaryColor}24`;
    for (const r of [70, 118, 166]) {
        ctx.beginPath(); ctx.arc(cx, cy, r, Math.PI * 0.15, Math.PI * 0.85); ctx.stroke();
    }
};

const drawBasketWeave = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Woven blocks: checkerboard cells alternate horizontal strands
    // and vertical strands, like a woven paper basket.
    const cw = 26, ch = 13;
    for (let row = 0, y = 0; y < B; row++, y += ch) {
        for (let col = 0, x = 0; x < W; col++, x += cw) {
            const horizontal = (row + col) % 2 === 0;
            ctx.fillStyle = horizontal ? `${primaryColor}12` : `${secondaryColor}10`;
            ctx.fillRect(x, y, cw, ch);
            ctx.strokeStyle = horizontal ? `${primaryColor}24` : `${secondaryColor}22`;
            ctx.lineWidth = 0.5;
            if (horizontal) {
                for (let s = 3; s < ch; s += 3.4) {
                    ctx.beginPath(); ctx.moveTo(x + 1, y + s); ctx.lineTo(x + cw - 1, y + s); ctx.stroke();
                }
            } else {
                for (let s = 3; s < cw; s += 3.4) {
                    ctx.beginPath(); ctx.moveTo(x + s, y + 1); ctx.lineTo(x + s, y + ch - 1); ctx.stroke();
                }
            }
        }
    }
};

const drawChevron = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Chevron bands: zigzag rows on an even pitch, alternate rows in
    // the secondary tone and phase-shifted.
    const pitch = 17, seg = 14, amp = 6;
    ctx.lineWidth = 0.9;
    for (let row = 0, y = 8; y < B + amp; row++, y += pitch) {
        ctx.strokeStyle = row % 2 === 0 ? `${primaryColor}2c` : `${secondaryColor}24`;
        const phase = (row % 2) * seg;
        ctx.beginPath();
        for (let x = -seg; x < W + seg; x += seg) {
            const midX = x + phase + seg / 2;
            if (x === -seg) ctx.moveTo(x + phase, y + amp);
            else ctx.lineTo(midX - seg / 2, y + amp);
            ctx.lineTo(midX, y);
            ctx.lineTo(midX + seg / 2, y + amp);
        }
        ctx.stroke();
    }
};

const drawFanScallop = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Overlapping scallop fans (fish-scale rows): semicircle arcs in
    // offset rows, double-ruled like engraved scale work.
    const r = 13;
    ctx.lineWidth = 0.6;
    for (let row = 0, y = 4; y < B + r; row++, y += r * 0.92) {
        const off = (row % 2) * r;
        for (let x = -r + off; x < W + r; x += r * 2) {
            ctx.strokeStyle = `${primaryColor}28`;
            ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI); ctx.stroke();
            ctx.strokeStyle = `${secondaryColor}1c`;
            ctx.beginPath(); ctx.arc(x, y, r * 0.68, 0, Math.PI); ctx.stroke();
        }
    }
};

const drawMarbleVein = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, accentColor } = g;
    // Veined marble: seeded random-walk veins that taper and throw
    // one branch each — real marble's fracture lines, not the soft
    // cloud wash of cloud_marble or the broken swirl of marble.
    const rnd = seeded(1234);
    const vein = (startX: number, startY: number, steps: number, tone: string, w0: number, seedBase: number) => {
        let x = startX, y = startY;
        let ang = rnd(seedBase) * Math.PI * 2;
        ctx.strokeStyle = tone;
        for (let s = 0; s < steps; s++) {
            const nx = x + Math.cos(ang) * 9;
            const ny = y + Math.sin(ang) * 5;
            ctx.lineWidth = Math.max(0.3, w0 * (1 - s / steps));
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(nx, ny); ctx.stroke();
            if (s === Math.floor(steps / 2)) {
                // One fine branch off the main vein.
                let bx = x, by = y, ba = ang + 0.9;
                ctx.lineWidth = 0.35;
                for (let b = 0; b < 7; b++) {
                    const bnx = bx + Math.cos(ba) * 7, bny = by + Math.sin(ba) * 4;
                    ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bnx, bny); ctx.stroke();
                    bx = bnx; by = bny; ba += (rnd(seedBase + 50 + b) - 0.5) * 0.7;
                }
            }
            x = nx; y = ny;
            ang += (rnd(seedBase + s) - 0.5) * 0.62;
        }
    };
    for (let i = 0; i < 7; i++) {
        vein(rnd(i * 3 + 1) * W, rnd(i * 3 + 2) * B, 26, 'rgba(96,110,128,0.30)', 1.7, i * 100 + 7);
    }
    for (let i = 0; i < 3; i++) {
        vein(rnd(900 + i * 3) * W, rnd(950 + i * 3) * B, 20, `${primaryColor}26`, 1.1, 500 + i * 100);
    }
    vein(rnd(2001) * W, rnd(2002) * B, 22, `${accentColor}30`, 0.9, 3000);
};

const drawGraniteSpeckle = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor } = g;
    // Granite: dense seeded mineral grains in three tones — dark
    // biotite flecks, pale quartz, and a whisper of the colourway.
    const rnd = seeded(4242);
    const tones = ['rgba(52,56,66,0.20)', 'rgba(255,255,255,0.28)', `${primaryColor}1f`, 'rgba(120,116,108,0.16)'];
    for (let i = 0; i < 2400; i++) {
        const x = rnd(i * 4 + 1) * W;
        const y = rnd(i * 4 + 2) * B;
        const s = 0.6 + rnd(i * 4 + 3) * 1.3;
        ctx.fillStyle = tones[Math.floor(rnd(i * 4 + 4) * tones.length) % tones.length];
        ctx.fillRect(x, y, s, s * (0.6 + rnd(i + 77) * 0.7));
    }
    // A few larger feldspar crystals, as in real granite.
    for (let i = 0; i < 46; i++) {
        const x = rnd(5000 + i * 3) * W, y = rnd(6000 + i * 3) * B;
        ctx.fillStyle = 'rgba(210,200,188,0.20)';
        ctx.fillRect(x, y, 2.4 + rnd(7000 + i) * 2.2, 1.6 + rnd(8000 + i) * 1.6);
    }
};

const drawHerringbone = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Herringbone twill: short diagonal strokes in blocks whose
    // direction reverses in a zigzag rhythm down the sheet.
    const bw = 30, bh = 15, stroke = 4.2;
    ctx.lineWidth = 0.8;
    for (let row = 0, y = 0; y < B + bh; row++, y += bh) {
        for (let col = 0, x = 0; x < W + bw; col++, x += bw) {
            const up = (Math.floor(col / 2) + row) % 2 === 0;
            ctx.strokeStyle = (col + row) % 2 === 0 ? `${primaryColor}22` : `${secondaryColor}1e`;
            for (let s = -bh; s < bw; s += stroke) {
                ctx.beginPath();
                if (up) { ctx.moveTo(x + s, y + bh); ctx.lineTo(x + s + bh, y); }
                else { ctx.moveTo(x + s, y); ctx.lineTo(x + s + bh, y + bh); }
                ctx.stroke();
            }
        }
    }
};

const drawSatinSheen = (ctx: Ctx, g: BackgroundGeom) => {
    const { width: W, textureBottom: B, primaryColor, secondaryColor } = g;
    // Satin sheen: broad soft diagonal light bands over the paper —
    // the quiet lustre of satin-finish security stock.
    const grad = ctx.createLinearGradient(0, 0, W * 0.7, B);
    grad.addColorStop(0, 'rgba(255,255,255,0.10)');
    grad.addColorStop(0.28, `${primaryColor}07`);
    grad.addColorStop(0.5, 'rgba(255,255,255,0.13)');
    grad.addColorStop(0.74, `${secondaryColor}07`);
    grad.addColorStop(1, 'rgba(255,255,255,0.09)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, B);
    // Two crisper sheen stripes riding the same diagonal.
    ctx.save();
    ctx.translate(W / 2, B / 2);
    ctx.rotate(-0.42);
    ctx.fillStyle = 'rgba(255,255,255,0.07)';
    ctx.fillRect(-W, -B * 0.52, W * 2, 26);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fillRect(-W, B * 0.18, W * 2, 44);
    ctx.restore();
};

const drawScenicField = (ctx: Ctx, g: BackgroundGeom) => {
    // ANB BANK SPECIMEN FACE (exact rebuild, 2026-11-04; source: the
    // owner's specimen photo, image-31.png top check). The face is
    // ONE scenic picture, not a texture wash: a pale-blue sky with
    // soft white clouds across the TOP of the check, a soft horizon
    // just above the pay-to row, and below it a GOLDEN WHEAT FIELD
    // whose grain — a dense stand of seeded wheat stalks with ear
    // ticks — is the design's yellow PATTERN. (The first ANB
    // build painted only a blue cloud band mid-face over cream
    // paper: the sky sat in the wrong place and the yellow field
    // was missing entirely — the owner's named defect, in his
    // words: "we just made the background the blue side but it's
    // not the yellow pattern on it.") The field fades to near-white
    // toward the MICR clear band, as the stock does. Seeded
    // throughout, so screen, re-render and export paint the
    // identical scene. Colours are the stock's own; the colourway
    // args are ignored for this design.
    const { width: W, textureBottom: B } = g;
    const rnd = seeded(20261105);
    let n = 0;
    const R = () => rnd(++n);
    // The horizon sits just above the pay-to row: on the specimen
    // (850x350 face, texture bottom 285 with the MICR band shown)
    // the sky/field meeting reads at ~y142 of the textured area.
    const horizon = Math.round(B * 0.5);

    // --- Sky -------------------------------------------------------------
    const sky = ctx.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, '#ffffff');
    sky.addColorStop(0.5, '#eef5fd');
    sky.addColorStop(1, '#dbedfa');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, horizon);

    // Clouds: clusters of soft white puffs over a faint blue-grey
    // shadow, spread across the whole sky — denser and larger
    // toward the horizon, as the stock prints them.
    for (let i = 0; i < 13; i++) {
        const cx = R() * W;
        const cy = 4 + R() * horizon * 0.94;
        const r = 36 + R() * 92 * (0.55 + (cy / horizon) * 0.6);
        const shadow = ctx.createRadialGradient(cx, cy + r * 0.2, 0, cx, cy + r * 0.2, r);
        shadow.addColorStop(0, 'rgba(110,150,205,0.11)');
        shadow.addColorStop(1, 'rgba(110,150,205,0)');
        ctx.fillStyle = shadow;
        ctx.beginPath(); ctx.ellipse(cx, cy + r * 0.2, r, r * 0.4, 0, 0, Math.PI * 2); ctx.fill();
        for (let j = 0; j < 4; j++) {
            const px = cx + (R() - 0.5) * r * 0.95;
            const py = cy + (R() - 0.5) * r * 0.2;
            const pr = r * (0.5 + R() * 0.32);
            const puff = ctx.createRadialGradient(px, py, 0, px, py, pr);
            puff.addColorStop(0, 'rgba(255,255,255,0.62)');
            puff.addColorStop(0.7, 'rgba(255,255,255,0.3)');
            puff.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = puff;
            ctx.beginPath(); ctx.ellipse(px, py, pr, pr * 0.42, 0, 0, Math.PI * 2); ctx.fill();
        }
    }
    // Thin high cirrus streaks.
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    for (let i = 0; i < 7; i++) {
        const cx = R() * W, cy = R() * horizon * 0.4, len = 60 + R() * 130;
        ctx.beginPath(); ctx.ellipse(cx, cy, len, 3.2, 0, 0, Math.PI * 2); ctx.fill();
    }

    // --- Golden field ------------------------------------------------------
    // Pale gold at the horizon, its richest in the upper-middle of
    // the field, then easing back and fading to near-white at the
    // bottom so the MICR clear band stays clean, as on the stock.
    const field = ctx.createLinearGradient(0, horizon, 0, B);
    field.addColorStop(0, '#f9e9b8');
    field.addColorStop(0.45, '#f5e0a0');
    field.addColorStop(0.78, '#f7e7b6');
    field.addColorStop(1, '#fdfbef');
    ctx.fillStyle = field;
    ctx.fillRect(0, horizon, W, B - horizon);

    // Horizon blend: cloud haze above the line, gold wash below it,
    // and a few flat puffs straddling the meeting — the stock's
    // horizon is a soft drift, never a rule.
    const haze = ctx.createLinearGradient(0, horizon - 38, 0, horizon + 2);
    haze.addColorStop(0, 'rgba(255,255,255,0)');
    haze.addColorStop(1, 'rgba(255,255,255,0.3)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, horizon - 38, W, 40);
    const blend = ctx.createLinearGradient(0, horizon - 2, 0, horizon + 32);
    blend.addColorStop(0, 'rgba(249,233,184,0)');
    blend.addColorStop(1, 'rgba(249,233,184,0.5)');
    ctx.fillStyle = blend;
    ctx.fillRect(0, horizon - 2, W, 34);
    for (let i = 0; i < 4; i++) {
        const cx = R() * W, cy = horizon + (R() - 0.5) * 14, r = 55 + R() * 80;
        const puff = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        puff.addColorStop(0, 'rgba(255,255,255,0.34)');
        puff.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = puff;
        ctx.beginPath(); ctx.ellipse(cx, cy, r, r * 0.3, 0, 0, Math.PI * 2); ctx.fill();
    }

    // Mottling: broad soft unevenness in the gold, so the field
    // reads as printed art rather than a flat fill.
    for (let i = 0; i < 20; i++) {
        const cx = R() * W, cy = horizon + R() * (B - horizon), r = 42 + R() * 110;
        const blob = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        const warm = R() < 0.5;
        blob.addColorStop(0, warm ? 'rgba(216,176,84,0.055)' : 'rgba(255,250,224,0.055)');
        blob.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = blob;
        ctx.beginPath(); ctx.ellipse(cx, cy, r, r * 0.55, 0, 0, Math.PI * 2); ctx.fill();
    }

    // THE YELLOW PATTERN — the field's wheat stand: seeded stalks,
    // each a near-vertical stem with three short ear ticks angled
    // up off its head, in deep-gold / mid-gold / pale-straw inks.
    // CORRECTIVE (2026-11-04, owner-verified defect): the first
    // pass drew 2.5-7-unit, 0.7-wide ticks at alpha 0.13/0.07 —
    // a ~4% luminance step over the field gradient, i.e. speckle
    // that no rasterizer can show: the live render read as a flat
    // golden wash and the yellow pattern was again missing. The
    // stalks below are sized and inked to actually print: stems
    // 7-21 units at width 1.15 with alpha 0.36-0.60 (a 30-60 unit
    // luminance step against the field), ears as diagonal ticks.
    // Depth is honest print behaviour: stalks grow taller toward
    // the viewer and their ink LIGHTENS toward the field's own
    // pale tone as they near the MICR band, so the stand fades
    // out exactly where the stock's does. Every stalk emits
    // exactly 4 strokes (1 stem + 3 ears) — the count is pinned.
    const STALKS = 1200;
    const INKS: Array<[number, number, number, number]> = [
        [146, 106, 32, 0.46],   // deep gold-brown
        [178, 134, 48, 0.40],   // mid gold
        [255, 244, 196, 0.36]   // pale straw (sunlit heads)
    ];
    ctx.lineCap = 'round';
    for (let i = 0; i < STALKS; i++) {
        const x = R() * W;
        const yBase = horizon + 4 + R() * (B - horizon - 6);
        const t = (yBase - horizon) / (B - horizon); // 0 horizon -> 1 MICR band
        // Taller toward the viewer, in soft drifts (a low sine
        // over x varies the stand's height like the printed art);
        // never pokes above the horizon.
        let len = (9 + R() * 9) * (0.72 + 0.45 * t) * (1 + 0.18 * Math.sin(x * 0.021 + t * 2.0));
        len = Math.min(len, yBase - horizon - 3);
        const lean = (R() - 0.5) * len * 0.26;
        const tone = R();
        const ink = tone < 0.42 ? INKS[0] : tone < 0.78 ? INKS[1] : INKS[2];
        // Ink lightens toward the field's own pale tone at BOTH
        // ends of the depth range: into the horizon haze (far
        // stalks) and into the MICR fade (near stalks).
        const fade = Math.max(0.85 * Math.pow(t, 1.6), t < 0.18 ? 0.55 * (1 - t / 0.18) : 0);
        const cr = Math.round(ink[0] + (247 - ink[0]) * fade);
        const cg = Math.round(ink[1] + (231 - ink[1]) * fade);
        const cb = Math.round(ink[2] + (182 - ink[2]) * fade);
        const alpha = ink[3] + R() * 0.14;
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${alpha.toFixed(3)})`;
        // Stem: one slightly bowed near-vertical stroke.
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.moveTo(x, yBase);
        ctx.quadraticCurveTo(x + lean * 0.3, yBase - len * 0.55, x + lean, yBase - len);
        ctx.stroke();
        // Ears: three short ticks hugging the head at ~33 degrees
        // — wheat awns, not branches — sides alternating.
        ctx.lineWidth = 0.9;
        for (let k = 0; k < 3; k++) {
            const f = 0.52 + k * 0.16;
            const sx = x + lean * f;
            const sy = yBase - len * f;
            const side = (k % 2 === 0 ? 1 : -1) * (R() < 0.82 ? 1 : -1);
            let tl = 1.9 + R() * 1.4;
            // Keep the tick tip below the horizon haze line.
            tl = Math.min(tl, Math.max(1.0, (sy - horizon - 1) / 0.95));
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(sx + side * tl * 0.62, sy - tl * 0.95);
            ctx.stroke();
        }
    }
};

const DRAWERS: Record<string, (ctx: Ctx, g: BackgroundGeom) => void> = {
    lattice: drawLattice,
    wave_ribbon: drawWaveRibbon,
    harlequin: drawHarlequin,
    pinstripe: drawPinstripe,
    dot_grid: drawDotGrid,
    contour: drawContour,
    starburst: drawStarburst,
    basket_weave: drawBasketWeave,
    chevron: drawChevron,
    fan_scallop: drawFanScallop,
    marble_vein: drawMarbleVein,
    granite_speckle: drawGraniteSpeckle,
    herringbone: drawHerringbone,
    satin_sheen: drawSatinSheen,
    scenic_field: drawScenicField
};

/**
 * Draw one of the new background designs. Clips to the area above
 * the MICR clear band (textureBottom) itself, so callers cannot
 * leak a design into the band. Returns false for unknown ids.
 */
export function drawBackgroundDesign(ctx: Ctx, id: string, geom: BackgroundGeom): boolean {
    const draw = DRAWERS[id];
    if (!draw) return false;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, geom.width, geom.textureBottom);
    ctx.clip();
    draw(ctx, geom);
    ctx.restore();
    return true;
}
